import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { TTableSort, TableSort } from 'src/components/table/thead/col';
import { HttpError, HttpService } from 'src/toolkit';

import { useAppSelector } from '../../hooks';
import { AccountTypeChangeSortCol, TAccountTypeChangeList, TAccountTypeChangeQuery, TAccountTypeChangeListResponse, TAccountTypeChangeSortCol, adaptAccountTypeChangeFromListResponse, adaptAccountTypeChangeQueryRequest } from '../../types';
import { BACKEND_ACCOUNT_TYPE_CHANGES } from '../../backend-paths';


export const accountTypeChangesGetApi = createAsyncThunk(
	`FETCH/${BACKEND_ACCOUNT_TYPE_CHANGES}`,
	(payload: TAccountTypeChangeQuery, thunkApi) => {
		const params = payload ? adaptAccountTypeChangeQueryRequest(payload) : {};

		return HttpService.get<TAccountTypeChangeListResponse>(BACKEND_ACCOUNT_TYPE_CHANGES, { params }).then(result => {
			return adaptAccountTypeChangeFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean,
	list: TAccountTypeChangeList,
	query: TAccountTypeChangeQuery,
	error?: HttpError,
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		sort: TableSort.DESC,
		sortCol: AccountTypeChangeSortCol.CREATED_AT,
	},
};

export const accountTypeChangesSlice = createSlice({
	name: 'accountTypeChangesSlice',
	initialState,
	reducers: {
		setAccountTypeChangesQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setAccountTypeChangesQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setAccountTypeChangesQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setAccountTypeChangesQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setAccountTypeChangesQuerySortCol(state, action: PayloadAction<TAccountTypeChangeSortCol>) {
			state.query.sortCol = action.payload;
		},

		setAccountTypeChangesQueryDateStart(state, action: PayloadAction<string>) {
			state.query.dateStart = action.payload;
		},

		setAccountTypeChangesQueryDateEnd(state, action: PayloadAction<string>) {
			state.query.dateEnd = action.payload;
		},

		clearAccountTypeChanges(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[accountTypeChangesGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountTypeChangesGetApi.fulfilled.type]: (state, action: PayloadAction<TAccountTypeChangeList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountTypeChangesGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountTypeChangeSelector = () => useAppSelector(state => state[accountTypeChangesSlice.name]);

export const { clearAccountTypeChanges, setAccountTypeChangesQueryLimit, setAccountTypeChangesQueryOffset, setAccountTypeChangesQuerySort, setAccountTypeChangesQuerySortCol, setAccountTypeChangesQuerySearch, setAccountTypeChangesQueryDateStart, setAccountTypeChangesQueryDateEnd } = accountTypeChangesSlice.actions;
